import { MIME_TYPE_MAP } from "@9amhealth/shared";

/**
 * MIME_TYPE_MAP is keyed by extension and maps several extensions onto the same
 * MIME value (`jpg`/`jpeg`, `html`/`htm`). Inverting it first-wins picks the
 * leading extension deterministically rather than relying on key order.
 */
const invertFirstWins = (map: Record<string, string>): Record<string, string> =>
  Object.entries(map).reduce<Record<string, string>>(
    (acc, [extension, mimeType]) =>
      mimeType in acc ? acc : { ...acc, [mimeType]: extension },
    {}
  );

const MIME_EXTENSIONS: Record<string, string> = {
  ...invertFirstWins(MIME_TYPE_MAP),
  // Absent from the shared map, which pmp also consumes.
  "image/heic": "heic",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx"
};

const EXTENSION_PATTERN = /\.([a-z0-9]{1,8})$/i;

/**
 * Android's FileProvider hands other apps a `content://` URI whose last path
 * segment is the on-disk file name. An app that receives that URI second-hand —
 * a PDF viewer passing it along to Drive, say — loses the originating intent's
 * explicit MIME type and falls back to sniffing the extension. A file we cache
 * for native opening therefore has to carry a real extension, or the receiving
 * app rejects it ("Couldn't load object").
 *
 * The MIME type wins over the file name because names like
 * `Lab Requisition 2026.08.27` would otherwise yield an extension of `27`.
 */
export const fileExtension = (
  filename: string,
  mimeType: string
): string | null => {
  const fromMimeType =
    MIME_EXTENSIONS[mimeType.split(";")[0].trim().toLowerCase()];
  if (fromMimeType) {
    return fromMimeType;
  }
  return EXTENSION_PATTERN.exec(filename)?.[1]?.toLowerCase() ?? null;
};

export const hasFileExtension = (path: string): boolean =>
  EXTENSION_PATTERN.test(path.split("/").pop() ?? "");
